import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '@/hooks/useApiMutation';
import { useApiQuery } from '@/hooks/useApiQuery';
import { apiGet, apiPatch, apiPost, apiPut, operationsClient } from '@/lib/api';
import { invoiceKeys, projectKeys } from '@/lib/queryKeys';
import { projectEndpoints } from '@/lib/api/endpoints';
import type {
  LienWaiver,
  PayApplication,
  PayApplicationDetail,
  ProjectContract,
} from '@/types/project';

export interface SovLineInput {
  itemNumber: string;
  description: string;
  scheduledValue: number;
  changeOrderId?: string;
}

export interface CreateContractInput {
  retainageWorkPct?: number;
  retainageStoredPct?: number;
  ownerName?: string;
  architectName?: string;
  contractorLicense?: string;
  contractDate?: string;
  netTermsDays?: number;
  lines?: SovLineInput[];
  importFromEstimateId?: string;
}

export function useProjectContract(
  projectId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  return useApiQuery<ProjectContract>({
    queryKey: projectKeys.contract(projectId ?? ''),
    queryFn: () => apiGet<ProjectContract>(operationsClient, projectEndpoints.contract(projectId!)),
    enabled: Boolean(projectId) && (options.enabled ?? true),
    // A project without AIA billing 404s here — that's the empty state.
    showErrorToast: false,
    retry: false,
  });
}

function useInvalidateBilling(projectId: string) {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({
      queryKey: projectKeys.contract(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: projectKeys.summary(projectId),
    });
    void queryClient.invalidateQueries({
      queryKey: projectKeys.payApplications(projectId),
    });
  };
}

export function useCreateContract(projectId: string) {
  const invalidate = useInvalidateBilling(projectId);
  return useApiMutation<ProjectContract, CreateContractInput>({
    mutationFn: (input) =>
      apiPost<ProjectContract, CreateContractInput>(
        operationsClient,
        projectEndpoints.contract(projectId),
        input,
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Schedule-of-values billing enabled');
    },
  });
}

export function useUpdateContract(projectId: string) {
  const invalidate = useInvalidateBilling(projectId);
  return useApiMutation<ProjectContract, Partial<CreateContractInput>>({
    mutationFn: (input) =>
      apiPatch<ProjectContract, Partial<CreateContractInput>>(
        operationsClient,
        projectEndpoints.contract(projectId),
        input,
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Contract updated');
    },
  });
}

export function useReplaceSovLines(projectId: string) {
  const invalidate = useInvalidateBilling(projectId);
  return useApiMutation<ProjectContract, SovLineInput[]>({
    mutationFn: (lines) =>
      apiPut<ProjectContract, { lines: SovLineInput[] }>(
        operationsClient,
        projectEndpoints.sovLines(projectId),
        { lines },
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Schedule of Values saved');
    },
  });
}

/** Replace a DRAFT Schedule of Values with an approved estimate's line items.
 *  The server owns the estimate→SoV mapping, so this posts the estimate id
 *  rather than expanding the lines here and letting the two drift. */
export function useImportSovFromEstimate(projectId: string) {
  const invalidate = useInvalidateBilling(projectId);
  return useApiMutation<ProjectContract, string>({
    mutationFn: (importFromEstimateId) =>
      apiPut<ProjectContract, { importFromEstimateId: string }>(
        operationsClient,
        projectEndpoints.sovLines(projectId),
        { importFromEstimateId },
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Schedule of Values imported from the estimate');
    },
  });
}

export function useAddSovLines(projectId: string) {
  const invalidate = useInvalidateBilling(projectId);
  return useApiMutation<ProjectContract, SovLineInput[]>({
    mutationFn: (lines) =>
      apiPost<ProjectContract, { lines: SovLineInput[] }>(
        operationsClient,
        projectEndpoints.sovLines(projectId),
        { lines },
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Lines added to Schedule of Values');
    },
  });
}

export function useProjectPayApplications(projectId: string | undefined) {
  return useApiQuery<PayApplication[]>({
    queryKey: projectKeys.payApplications(projectId ?? ''),
    queryFn: () =>
      apiGet<PayApplication[]>(operationsClient, projectEndpoints.payApplications(projectId!)),
    enabled: Boolean(projectId),
  });
}

export function usePayApplication(appId: string | undefined) {
  return useApiQuery<PayApplicationDetail>({
    queryKey: projectKeys.payApplication(appId ?? ''),
    queryFn: () =>
      apiGet<PayApplicationDetail>(operationsClient, projectEndpoints.payApplication(appId!)),
    enabled: Boolean(appId),
  });
}

export function useCreatePayApplication(projectId: string) {
  const invalidate = useInvalidateBilling(projectId);
  return useApiMutation<PayApplicationDetail, { periodFrom: string; periodTo: string }>({
    mutationFn: (input) =>
      apiPost<PayApplicationDetail, typeof input>(
        operationsClient,
        projectEndpoints.payApplications(projectId),
        input,
      ),
    onSuccess: () => {
      invalidate();
      toast.success('Pay application opened');
    },
  });
}

export interface PayAppLineEntry {
  sovLineId: string;
  workThisPeriod: number;
  storedMaterials: number;
}

export function useSavePayApplicationLines(appId: string, projectId: string) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateBilling(projectId);
  return useApiMutation<PayApplicationDetail, PayAppLineEntry[]>({
    mutationFn: (entries) =>
      apiPatch<PayApplicationDetail, { entries: PayAppLineEntry[] }>(
        operationsClient,
        projectEndpoints.payApplicationLines(appId),
        { entries },
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.payApplication(appId),
      });
      invalidate();
      toast.success('Pay application saved');
    },
  });
}

export function usePayApplicationTransition(
  appId: string,
  projectId: string,
  action: 'submit' | 'approve' | 'invoice' | 'void' | 'notarized',
) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateBilling(projectId);
  const endpointByAction = {
    submit: projectEndpoints.payApplicationSubmit,
    approve: projectEndpoints.payApplicationApprove,
    invoice: projectEndpoints.payApplicationInvoice,
    void: projectEndpoints.payApplicationVoid,
    notarized: projectEndpoints.payApplicationNotarized,
  } as const;
  const successByAction: Record<typeof action, string> = {
    submit: 'Pay application submitted',
    approve: 'Pay application approved',
    invoice: 'Invoice generated',
    void: 'Pay application voided',
    notarized: 'Notarization recorded',
  };
  // `notarized` optionally carries { notarizedDocumentId } from the upload
  // flow; the other transitions post an empty body.
  return useApiMutation<PayApplicationDetail, Record<string, unknown> | void>({
    mutationFn: (body) =>
      apiPost<PayApplicationDetail, Record<string, unknown>>(
        operationsClient,
        endpointByAction[action](appId),
        body ?? {},
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.payApplication(appId),
      });
      invalidate();
      if (action === 'invoice') {
        void queryClient.invalidateQueries({ queryKey: invoiceKeys.all() });
      }
      toast.success(successByAction[action]);
    },
  });
}

export function useProjectLienWaivers(projectId: string | undefined) {
  return useApiQuery<LienWaiver[]>({
    queryKey: projectKeys.lienWaivers(projectId ?? ''),
    queryFn: () => apiGet<LienWaiver[]>(operationsClient, projectEndpoints.lienWaivers(projectId!)),
    enabled: Boolean(projectId),
  });
}

export interface CreateLienWaiverInput {
  payApplicationId?: string;
  type: LienWaiver['type'];
  amount?: number;
  throughDate?: string;
  notes?: string;
}

export function useUpdateLienWaiver(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<LienWaiver, { id: string; status?: LienWaiver['status']; notes?: string }>({
    mutationFn: ({ id, ...input }) =>
      apiPatch<LienWaiver, { status?: string; notes?: string }>(
        operationsClient,
        projectEndpoints.lienWaiver(id),
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.lienWaivers(projectId),
      });
      // The card's "released to date" line reads the project summary, so the
      // aggregate has to be refetched alongside the list a status change
      // moves a waiver within — otherwise the table updates and the running
      // total beside it stays stale until a full reload.
      void queryClient.invalidateQueries({
        queryKey: projectKeys.summary(projectId),
      });
      toast.success('Lien waiver updated');
    },
  });
}

export function useCreateLienWaiver(projectId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<LienWaiver, CreateLienWaiverInput>({
    mutationFn: (input) =>
      apiPost<LienWaiver, CreateLienWaiverInput>(
        operationsClient,
        projectEndpoints.lienWaivers(projectId),
        input,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: projectKeys.lienWaivers(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.summary(projectId),
      });
      toast.success('Lien waiver recorded');
    },
  });
}
